/**
 * RuleEngine Mixin for Vue.js
 * Provides pattern-based rule processing functionality
 */

import { roundNumberBySettings } from "@/utils/numberFormatter";

export default {
  data() {
    return {
      rulesengines: [],
      isProcessing: false,
      destinationFields: new Set(),
      executedRules: new Set(),
      processingFields: new Set(),
      // Control de ejecución para evitar bucles infinitos pero permitir re-ejecución
      ruleExecutionCount: new Map(),
      maxRuleExecutions: 10, // Permitir más ejecuciones por regla
      currentExecutionCycle: 0,
      // Flag para evitar recursión
      isUpdatingCalculatedField: false,
    };
  },

  methods: {
    /**
     * Redondea según decimales del formato de números (Settings → numberFormat).
     * @param {number} value
     * @returns {number}
     */
    roundToTwo(value) {
      return roundNumberBySettings(value);
    },

    /**
     * Add a new rule to the engine
     * @param {Object} rule - The rule object containing pattern and destination
     * @param {string} rule.pattern - The pattern to match
     * @param {string} rule.destination - The destination field ID where values will be inserted
     * @param {string} rule.condition - The condition to evaluate before applying the rule
     * @param {string} rule.validationMessage - The message to display if the condition is not met
     */
    addRule(rule) {
      this.rulesengines.push({
        pattern: rule.pattern,
        destination: rule.destination,
        condition: rule.condition,
        validationMessage: rule.validationMessage,
      });

      // Create regex for destination pattern
      const destRegex = new RegExp(
        "^" + rule.destination.replace(/\*/g, "\\d+") + "$"
      );

      // Find all inputs that match the destination pattern
      const inputs = document.querySelectorAll("input");
      inputs.forEach((input) => {
        if (destRegex.test(input.id)) {
          input.classList.add("calculated-field");
          // input.readOnly = true;
          input.title = "Campo calculado automáticamente";
          this.destinationFields.add(input.id);
        }
      });
    },

    /**
     * Convert a pattern to a regular expression
     * @param {string} pattern - The pattern to convert
     * @returns {RegExp} The regular expression
     */
    patternToRegex(pattern) {
      if (pattern.includes("X") || pattern.includes("/")) {
        const regexPattern = pattern
          .replace(/N/g, "\\d+")
          .replace(/-/g, "\\-")
          .replace(/X/g, "\\s*X\\s*")
          .replace(/\//g, "\\s*\\/\\s*");
        return new RegExp(`^${regexPattern}$`);
      }

      const regexPattern = pattern.replace(/\*/g, "\\d+").replace(/-/g, "\\-");
      return new RegExp(`^${regexPattern}$`);
    },

    /**
     * Find all input elements that match a pattern
     * @param {string} pattern - The pattern to match
     * @returns {Array} Array of matching input elements
     */
    findMatchingInputs(pattern) {
      const regex = this.patternToRegex(pattern);
      const inputs = document.querySelectorAll("input");
      return Array.from(inputs).filter((input) => regex.test(input.id));
    },

    /**
     * Evaluate a complex expression with multiple operators
     * @param {string} expression - The expression to evaluate
     * @param {string} destinationField - The destination field ID to show calculation
     * @returns {number} The result of the evaluation
     */
    evaluateExpression(expression, destinationField = null) {
      

      if (expression.startsWith("dateDiff(")) {
        try {
          const matches = expression.match(/dateDiff\((.*?),\s*(.*?)\)/);
          ////console.log('📆 Detected dateDiff match:', matches);

          if (matches) {
            const [_, field1, field2] = matches;
            //console.log('📆 Fields:', field1, field2);

            const date1Element = document.getElementById(field1);
            const date2Element = document.getElementById(field2);

            //console.log('📅 Element values:', date1Element?.value, date2Element?.value);

            if (date1Element && date2Element) {
              if (
                date1Element.type !== "date" ||
                date2Element.type !== "date"
              ) {
                console.error("❌ Los campos deben ser de tipo fecha");
                return 0;
              }

              const date1 = new Date(date1Element.value);
              const date2 = new Date(date2Element.value);

              //console.log('📅 Parsed dates:', date1, date2);

              if (isNaN(date1.getTime()) || isNaN(date2.getTime())) {
                console.error("❌ Las fechas no son válidas");
                return 0;
              }

              const diffTime = Math.abs(date2 - date1);
              const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
              //console.log('✅ Date diff (days):', diffDays);

              // Show calculation
              if (destinationField) {
                const destElement = document.getElementById(destinationField);
                if (destElement) {
                  const formula = `dateDiff(${date1Element.value}, ${date2Element.value}) = ${diffDays} días`;
                  this.showCalculation(destElement, formula);
                }
              }

              return diffDays;
            }
          }
          return 0;
        } catch (error) {
          console.error("❌ Error calculating date difference:", error);
          return 0;
        }
      }

      let processedExpression = expression.replace(/X/g, "*");
      //console.log('🧮 Processed expression (X → *):', processedExpression);

      // Primero, identificar todos los campos posibles en la expresión
      const fieldMatches =
        processedExpression.match(/[a-zA-Z][a-zA-Z0-9_\-]*/g) || [];
      const fieldMap = new Map();

      // Crear un mapa de campos y sus valores
      fieldMatches.forEach((fieldId) => {
        if (!isNaN(fieldId)) return; // Saltar números

        const field = document.getElementById(fieldId);
        if (field) {
          const value = parseFloat(field.value) || 0;
          fieldMap.set(fieldId, value);
          this.markSourceField(fieldId);
        } else {
          fieldMap.set(fieldId, 0);
        }
      });

      // Reemplazar campos con sus valores, ordenando por longitud para evitar reemplazos parciales
      let evaluatedExpression = processedExpression;
      const sortedFields = Array.from(fieldMap.keys()).sort(
        (a, b) => b.length - a.length
      );

      sortedFields.forEach((fieldId) => {
        const value = fieldMap.get(fieldId);
        // Usar regex para reemplazar solo el campo completo
        const fieldRegex = new RegExp(
          `\\b${fieldId.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\b`,
          "g"
        );
        evaluatedExpression = evaluatedExpression.replace(fieldRegex, value);
      });

      //console.log('🧪 Evaluated expression:', evaluatedExpression);

      try {
        if (!/^[0-9+\-*/().\s]+$/.test(evaluatedExpression)) {
          throw new Error("❌ Invalid expression format");
        }

        const evaluateSimple = (expr) => {
          //console.log('🔢 Evaluating simple expression:', expr);
          const parts = expr.split(/([+\-])/).filter((part) => part.trim());
          let result = 0;

          for (let i = 0; i < parts.length; i++) {
            const part = parts[i].trim();
            if (part === "+" || part === "-") continue;

            const multDivParts = part.split(/([*\/])/).filter((p) => p.trim());
            let partResult = parseFloat(multDivParts[0]) || 0;

            for (let j = 1; j < multDivParts.length; j += 2) {
              const operator = multDivParts[j];
              const operand = parseFloat(multDivParts[j + 1]) || 0;

              if (operator === "*") {
                partResult *= operand;
              } else if (operator === "/") {
                partResult = operand !== 0 ? partResult / operand : partResult;
              }
            }

            if (i === 0) {
              result = partResult;
            } else {
              const operator = parts[i - 1];
              if (operator === "+") {
                result += partResult;
              } else if (operator === "-") {
                result -= partResult;
              }
            }
          }

          //console.log('🧾 Simple expression result:', result);
          return result;
        };

        const evaluateFull = (expr) => {
          //console.log('📚 Evaluating full expression:', expr);
          while (expr.includes("(")) {
            expr = expr.replace(/\(([^()]+)\)/g, (match, innerExpr) => {
              const val = evaluateSimple(innerExpr);
              //console.log(`🧩 Parenthesis evaluated: (${innerExpr}) →`, val);
              return val;
            });
          }
          return evaluateSimple(expr);
        };

        const result = evaluateFull(evaluatedExpression);
        const rounded = this.roundToTwo(result);
        //console.log('✅ Final result (rounded):', rounded);

        // Show calculation
        if (destinationField) {
          const destElement = document.getElementById(destinationField);
          if (destElement) {
            const formula = `${evaluatedExpression} = ${rounded}`;
            this.showCalculation(destElement, formula);
          }
        }

        return rounded;
      } catch (error) {
        console.error("❌ Error evaluating expression:", error);
        return 0;
      }
    },

    /**
     * Show calculation formula next to a field
     * @param {HTMLElement} field - The field element
     * @param {string} formula - The calculation formula with values
     */
    showCalculation(field, formula) {
      // Remove existing calculation display
      const existingDisplay = field.parentNode.querySelector(
        ".calculation-display"
      );
      if (existingDisplay) {
        existingDisplay.remove();
      }

      // Create calculation display
      const display = document.createElement("span");
      display.className = "calculation-display";
      display.textContent = formula;
      display.style.cssText = `
        margin-left: 10px;
        font-size: 0.9em;
        color: #666;
        font-style: italic;
      `;

      // Insert after the field
      if (field.nextSibling) {
        field.parentNode.insertBefore(display, field.nextSibling);
      } else {
        field.parentNode.appendChild(display);
      }
    },

    /**
     * Mark a field as a source field (participates in calculations)
     * @param {string} fieldId - The field ID to mark
     */
    markSourceField(fieldId) {
      const field = document.getElementById(fieldId);
      if (field && !this.destinationFields.has(fieldId)) {
        // Add source field indicator if not already present
        if (!field.parentNode.querySelector(".source-indicator")) {
          const indicator = document.createElement("span");
          indicator.className = "source-indicator";

          // Insert after the field
          if (field.nextSibling) {
            field.parentNode.insertBefore(indicator, field.nextSibling);
          } else {
            field.parentNode.appendChild(indicator);
          }
        }
      }
    },

    /**
     * Clear source field indicators
     */
    clearSourceFieldIndicators() {
      const sourceFields = document.querySelectorAll(".source-field");
      sourceFields.forEach((field) => {
        field.classList.remove("source-field");
        const indicator = field.parentNode.querySelector(".source-indicator");
        if (indicator) {
          indicator.remove();
        }
      });
    },

    /**
     * Evaluate a condition expression
     * @param {string} condition - The condition to evaluate
     * @returns {boolean} The result of the condition
     */
    evaluateCondition(rule) {
      if (!rule.condition) return true;

      try {
        const [field, operator, value] = rule.condition.split(" ");
        let fieldElement = document.getElementById(field);

        if (!fieldElement) {
          return false;
        }

        let fieldValue;
        if (fieldElement.type === "checkbox") {
          fieldValue = fieldElement.checked;
        } else {
          fieldValue = fieldElement.value;
        }


        let comparisonValue;
        if (value === "true" || value === "on") {
          comparisonValue = true;
        } else if (value === "false" || value === "off") {
          comparisonValue = false;
        } else if (!isNaN(value)) {
          comparisonValue = parseFloat(value);
        } else {
          comparisonValue = value;
        }

        let processedFieldValue;
        if (fieldElement.type === "checkbox") {
          processedFieldValue = fieldElement.checked;
        } else {
          if (fieldValue === "true" || fieldValue === "on") {
            processedFieldValue = true;
          } else if (fieldValue === "false" || fieldValue === "off") {
            processedFieldValue = false;
          } else if (!isNaN(fieldValue)) {
            processedFieldValue = parseFloat(fieldValue);
          } else {
            processedFieldValue = fieldValue;
          }
        }

        let result;
        switch (operator) {
          case "===":
            result = processedFieldValue === comparisonValue;
            break;
          case "!==":
            result = processedFieldValue !== comparisonValue;
            break;
          case ">":
            result = processedFieldValue > comparisonValue;
            break;
          case "<":
            result = processedFieldValue < comparisonValue;
            break;
          case ">=":
            result = processedFieldValue >= comparisonValue;
            break;
          case "<=":
            result = processedFieldValue <= comparisonValue;
            break;
          default:
            result = false;
        }

        // Agregar o remover la clase invalid-field según el resultado
        if (result) {
          fieldElement.classList.add("invalid-field");
          // Buscar la regla que tiene esta condición
          if (rule && rule.validationMessage) {
            // Crear un ID único para el mensaje de error basado en el campo objetivo
            const errorId = `error-${rule.destination.replace(
              /[^a-zA-Z0-9]/g,
              "_"
            )}`;

            // Buscar si ya existe un mensaje de error para esta regla específica
            let errorDiv = fieldElement.parentNode.querySelector(
              `[data-error-id="${errorId}"]`
            );

            if (!errorDiv) {
              errorDiv = document.createElement("div");
              errorDiv.className =
                "invalid-feedback alert alert-danger px-2 py-1 d-block";
              errorDiv.setAttribute("data-error-id", errorId);
              fieldElement.parentNode.insertBefore(
                errorDiv,
                fieldElement.nextSibling
              );
            }
            errorDiv.textContent = rule.validationMessage;
          }
        } else {
          // Remover solo el mensaje de error específico de esta regla
          if (rule && rule.validationMessage) {
            const errorId = `error-${rule.destination.replace(
              /[^a-zA-Z0-9]/g,
              "_"
            )}`;
            const errorDiv = fieldElement.parentNode.querySelector(
              `[data-error-id="${errorId}"]`
            );
            if (errorDiv) {
              errorDiv.remove();
            }
          }

          // Solo remover la clase invalid-field si no hay otros mensajes de error
          const remainingErrors =
            fieldElement.parentNode.querySelectorAll(".invalid-feedback");
          if (remainingErrors.length === 0) {
            fieldElement.classList.remove("invalid-field");
          }

          if (!rule.validationMessage) {
            const destinationField = document.getElementById(rule.destination);
            destinationField.value = null;
          }
        }

        return result;
      } catch (error) {
        console.error("Error evaluating condition:", error);
        return false;
      }
    },

    /**
     * Highlight a field temporarily to indicate it was modified
     * @param {HTMLElement} field - The field to highlight
     */
    highlightField(field) {
      field.classList.add("field-highlight"); 
      field.classList.add("calculated-field");
      setTimeout(() => {
        field.classList.remove("field-highlight");
      }, 500);
    },

    /**
     * Process nested patterns where the first wildcard must match
     * @param {string} pattern - The pattern to match
     * @param {string} destination - The destination pattern
     */
    processNestedPattern(pattern, destination) {
      const patternParts = pattern.split("-");
      const destinationParts = destination.split("-");

      const sourceRegex = new RegExp(
        "^" +
          patternParts
            .map((part, index) => {
              if (part === "*") {
                return "(\\d+)";
              }
              return part.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
            })
            .join("-") +
          "$"
      );

      const destRegex = new RegExp(
        "^" +
          destinationParts
            .map((part, index) => {
              if (part === "*") {
                return "(\\d+)";
              }
              return part.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
            })
            .join("-") +
          "$"
      );

      const inputs = document.querySelectorAll("input");
      const groupedValues = new Map();

      inputs.forEach((input) => {
        const sourceMatch = input.id.match(sourceRegex);
        if (sourceMatch) {
          const firstNumber = sourceMatch[1];
          const value = parseFloat(input.value) || 0;

          if (!groupedValues.has(firstNumber)) {
            groupedValues.set(firstNumber, []);
          }
          groupedValues.get(firstNumber).push(value);
        }
      });

      groupedValues.forEach((values, firstNumber) => {
        const destId = destination.replace("*", firstNumber);
        const destElement = document.getElementById(destId);

        if (destElement) {
          if (this.processingFields.has(destId)) {
            return;
          }

          this.processingFields.add(destId);

          try {
            const total = this.roundToTwo(
              values.reduce((sum, val) => sum + val, 0)
            );

            if (parseFloat(destElement.value) !== total) {
              let vueComponent = null;
              let currentElement = destElement;

              while (currentElement && !vueComponent) {
                if (currentElement.__vue__) {
                  vueComponent = currentElement.__vue__;
                  break;
                }
                currentElement = currentElement.parentElement;
              }

              // Usar updateFieldValue para actualizar correctamente el campo y notificar a Vue
              this.updateFieldValue(destId, total, destElement);

              destElement.classList.add("calculated-field");
              //destElement.readOnly = true;
              destElement.title = "";
              this.destinationFields.add(destId);

              // Show calculation formula
              //const formula = `Suma de ${values.length} campos = ${total}`;
              const formula = "";
              this.showCalculation(destElement, formula);

              this.highlightField(destElement);
            }
          } finally {
            this.processingFields.delete(destId);
          }
        }
      });
    },

    /**
     * Process all rules in the engine
     */
    processRules() {
      // Solo evitar recursión, no bloquear re-ejecución
      if (this.isUpdatingCalculatedField) {
        return;
      }

      this.isProcessing = true;
      this.currentExecutionCycle++;

      try {
        this.rulesengines.forEach((rule, index) => {
          const ruleId = `${rule.pattern}-${rule.destination}-${rule.condition}`;

          // Permitir re-ejecución de reglas cuando cambien los campos fuente
          // Solo verificar que no haya bucles infinitos
          if (this.detectInfiniteLoop(ruleId)) {
            return;
          }

              
          if(!this.evaluateCondition(rule)) {
            
          }else{

              // Marcar la regla como ejecutada en este ciclo
              this.executedRules.add(ruleId);
              this.ruleExecutionCount.set(
                ruleId,
                (this.ruleExecutionCount.get(ruleId) || 0) + 1
              );

              const patternAsterisks = (rule.pattern.match(/\*/g) || []).length;
              const destAsterisks = (rule.destination.match(/\*/g) || []).length;

              if (patternAsterisks > 1 && destAsterisks === 1) {
                this.processNestedPattern(rule.pattern, rule.destination);
              } else if (
                rule.pattern.includes("X") ||
                rule.pattern.includes("/") ||
                rule.pattern.includes("(")
              ) {
                if (rule.pattern.includes("X") && rule.pattern.includes("*")) {
                  try {
                    const [firstPattern, secondPattern] = rule.pattern
                      .split("X")
                      .map((p) => p.trim());

                    const wildcardRegex = /\*/g;
                    const firstNumbers = firstPattern.match(/\d+/g) || [];
                    const secondNumbers = secondPattern.match(/\d+/g) || [];

                    const inputs = document.querySelectorAll("input");
                    const matchingInputs = Array.from(inputs).filter((input) => {
                      const matches =
                        input.id.startsWith(firstPattern.split("*")[0]) &&
                        input.id.endsWith(firstPattern.split("*").pop());
                      return matches;
                    });

                    matchingInputs.forEach((firstInput) => {
                      const firstIdNumbers = firstInput.id.match(/\d+/g) || [];

                      let secondFieldId = secondPattern;
                      firstIdNumbers.forEach((num, index) => {
                        secondFieldId = secondFieldId.replace("*", num);
                      });

                      let destinationFieldId = rule.destination;
                      firstIdNumbers.forEach((num, index) => {
                        destinationFieldId = destinationFieldId.replace("*", num);
                      });

                      const secondField = document.getElementById(secondFieldId);
                      const destinationField =
                        document.getElementById(destinationFieldId);

                      if (firstInput && secondField && destinationField) {
                        const firstValue = parseFloat(firstInput.value) || 0;
                        const secondValue = parseFloat(secondField.value) || 0;
                        const result = this.roundToTwo(firstValue * secondValue);

                        // Mark source fields
                        this.markSourceField(firstInput.id);
                        this.markSourceField(secondField.id);

                        if (parseFloat(destinationField.value) !== result) {
                          destinationField.classList.add("calculated-field");
                          //destinationField.readOnly = true;
                          destinationField.title =
                            "Campo calculado automáticamente";
                          this.destinationFields.add(destinationFieldId);

                          // Show calculation formula
                          const formula = `${firstValue} × ${secondValue} = ${result}`;
                          this.showCalculation(destinationField, formula);

                          this.highlightField(destinationField);

                          // Usar updateFieldValue para actualizar correctamente el campo y notificar a Vue
                          this.updateFieldValue(destinationFieldId, result, destinationField);
                        }
                      }
                    });
                  } catch (error) {
                    console.error("Error processing multiplication rule:", error);
                  }
                } else if (
                  rule.pattern.includes("X") &&
                  rule.pattern.includes("N")
                ) {
                  const patternParts = rule.pattern.split("X");
                  const basePattern = patternParts[0].trim();
                  const basePatternRegex = basePattern.replace("N", "(\\d+)");
                  const regex = new RegExp(`^${basePatternRegex}$`);

                  const inputs = document.querySelectorAll("input");
                  const matchingFields = new Set();

                  inputs.forEach((input) => {
                    const match = input.id.match(regex);
                    if (match) {
                      matchingFields.add(match[1]);
                    }
                  });

                  matchingFields.forEach((fieldNum) => {
                    const firstFieldId = basePattern.replace("N", fieldNum);
                    const secondFieldId = patternParts[1]
                      .trim()
                      .replace("N", fieldNum);
                    const destinationFieldId = rule.destination.replace(
                      "N",
                      fieldNum
                    );

                    const firstInput = document.getElementById(firstFieldId);
                    const secondInput = document.getElementById(secondFieldId);
                    const destinationInput =
                      document.getElementById(destinationFieldId);

                    if (firstInput && secondInput && destinationInput) {
                      const firstValue = parseFloat(firstInput.value) || 0;
                      const secondValue = parseFloat(secondInput.value) || 0;
                      const result = this.roundToTwo(firstValue * secondValue);

                      // Mark source fields
                      this.markSourceField(firstFieldId);
                      this.markSourceField(secondFieldId);

                      if (parseFloat(destinationInput.value) !== result) {
                        this.highlightField(destinationInput);

                        // Usar updateFieldValue para actualizar correctamente el campo y notificar a Vue
                        const destFieldId = destinationInput.id || rule.destination;
                        this.updateFieldValue(destFieldId, result, destinationInput);
                      }
                    }
                  });
                } else if (
                  rule.pattern.includes("(") ||
                  rule.pattern.split("X").length > 2
                ) {
                  const destinationElement = document.getElementById(
                    rule.destination
                  );

                  if (destinationElement) {
                    const result = this.evaluateExpression(
                      rule.pattern,
                      rule.destination
                    );

                    if (parseFloat(destinationElement.value) !== result) {
                      this.highlightField(destinationElement);

                      // Usar updateFieldValue para actualizar correctamente el campo y notificar a Vue
                      this.updateFieldValue(rule.destination, result, destinationElement);
                    }
                  }
                } else if (rule.pattern.includes("X")) {
                  const parts = rule.pattern.split("X").map((p) => p.trim());
                  const firstField = parts[0];
                  const secondField = parts[1];
                  const destinationField = rule.destination;

                  const firstInput = document.getElementById(firstField);
                  if (!firstInput) {
                    return;
                  }

                  // Mark first field as source
                  this.markSourceField(firstField);

                  let secondValue;
                  if (!isNaN(secondField)) {
                    secondValue = parseFloat(secondField);
                  } else {
                    const secondInput = document.getElementById(secondField);
                    if (!secondInput) {
                      return;
                    }
                    secondValue = parseFloat(secondInput.value) || 0;

                    // Mark second field as source
                    this.markSourceField(secondField);
                  }

                  const destinationInput =
                    document.getElementById(destinationField);
                  if (!destinationInput) {
                    return;
                  }

                  const firstValue = parseFloat(firstInput.value) || 0;
                  const result = this.roundToTwo(firstValue * secondValue);

                  if (parseFloat(destinationInput.value) !== result) {
                      destinationInput.classList.add("calculated-field");
                      //destinationInput.readOnly = true;
                      destinationInput.title = "Campo calculado automáticamente";
                      this.destinationFields.add(destinationField);
                      // Mostrar fórmula SIEMPRE
                      const formula = `${firstValue} × ${secondValue} = ${result}`;
                      this.showCalculation(destinationInput, formula);
                      this.highlightField(destinationInput);
                      
                      // Usar updateFieldValue para actualizar correctamente el campo y notificar a Vue
                      this.updateFieldValue(destinationField, result, destinationInput);
                  } else {
                    // Si el valor no cambió, igual mostrar la fórmula
                    const formula = `${firstValue} × ${secondValue} = ${result}`;
                    this.showCalculation(destinationInput, formula);
                  }
                } else if (
                  rule.pattern.includes("(") ||
                  rule.pattern.includes("/")
                ) {
                  const destinationElement = document.getElementById(
                    rule.destination
                  );

                  if (destinationElement) {
                    const result = this.evaluateExpression(
                      rule.pattern,
                      rule.destination
                    );

                    if (parseFloat(destinationElement.value) !== result) {
                      this.highlightField(destinationElement);

                      // Usar updateFieldValue para actualizar correctamente el campo y notificar a Vue
                      this.updateFieldValue(rule.destination, result, destinationElement);
                    }
                  }
                }
              } else if (rule.pattern.includes("+")) {
                const fields = rule.pattern.split("+").map((field) => field.trim());
                const destinationElement = document.getElementById(
                  rule.destination
                );

                if (destinationElement) {
                  let total = 0;
                  let values = [];
                  fields.forEach((field) => {
                    const input = document.getElementById(field);
                    let value = 0;
                    if (input) {
                      value = parseFloat(input.value) || 0;
                      this.markSourceField(field);
                    }
                    values.push(value);
                    total += value;
                  });
                  total = this.roundToTwo(total);
                  if (parseFloat(destinationElement.value) !== total) {
                    destinationElement.classList.add("calculated-field");
                    //destinationElement.readOnly = true;
                    destinationElement.title = "Campo calculado automáticamente";
                    this.destinationFields.add(rule.destination);
                    // Mostrar fórmula SIEMPRE
                    const formula = `${values.join(" + ")} = ${total}`;
                    this.showCalculation(destinationElement, formula);
                    this.highlightField(destinationElement);
                    
                    // Usar updateFieldValue para actualizar correctamente el campo y notificar a Vue
                    this.updateFieldValue(rule.destination, total, destinationElement);
                  } else {
                    // Si el valor no cambió, igual mostrar la fórmula
                    const formula = `${values.join(" + ")} = ${total}`;
                    this.showCalculation(destinationElement, formula);
                  }
                }
              } else if (rule.pattern.includes(" - ")) {
                const [minuend, subtrahend] = rule.pattern
                  .split(" - ")
                  .map((field) => field.trim());
                const minuendInput = document.getElementById(minuend);
                const subtrahendInput = document.getElementById(subtrahend);
                const destinationElement = document.getElementById(
                  rule.destination
                );

                if (minuendInput && subtrahendInput && destinationElement) {
                  const minuendValue = parseFloat(minuendInput.value) || 0;
                  const subtrahendValue = parseFloat(subtrahendInput.value) || 0;
                  const result = this.roundToTwo(minuendValue - subtrahendValue);

                  // Mark source fields
                  this.markSourceField(minuend);
                  this.markSourceField(subtrahend);

                  if (parseFloat(destinationElement.value) !== result) {
                    destinationElement.classList.add("calculated-field");
                    //destinationElement.readOnly = true;
                    destinationElement.title = "Campo calculado automáticamente";
                    this.destinationFields.add(rule.destination);
                    // Mostrar fórmula SIEMPRE
                    const formula = `${minuendValue} - ${subtrahendValue} = ${result}`;
                    this.showCalculation(destinationElement, formula);
                    this.highlightField(destinationElement);

                    // Usar updateFieldValue para actualizar correctamente el campo y notificar a Vue
                    this.updateFieldValue(rule.destination, result, destinationElement);
                  } else {
                    // Si el valor no cambió, igual mostrar la fórmula
                    const formula = `${minuendValue} - ${subtrahendValue} = ${result}`;
                    this.showCalculation(destinationElement, formula);
                  }
                }
              } else if (rule.pattern.includes("(") || rule.pattern.includes(")")) {
                // Procesar expresiones con paréntesis usando evaluateExpression
                const destinationElement = document.getElementById(
                  rule.destination
                );

                if (destinationElement) {
                  const result = this.evaluateExpression(
                    rule.pattern,
                    rule.destination
                  );

                  if (parseFloat(destinationElement.value) !== result) {
                    destinationElement.classList.add("calculated-field");
                    //destinationElement.readOnly = true;
                    destinationElement.title = "Campo calculado automáticamente";
                    this.destinationFields.add(rule.destination);

                    this.highlightField(destinationElement);

                    // Usar updateFieldValue para actualizar correctamente el campo y notificar a Vue
                    this.updateFieldValue(rule.destination, result, destinationElement);
                  }
                }
              } else {
                const matchingInputs = this.findMatchingInputs(rule.pattern);
                const destinationElement = document.getElementById(
                  rule.destination
                );

                if (destinationElement && matchingInputs.length > 0) {
                  const values = matchingInputs
                    .map((input) => input.value)
                    .filter(
                      (value) =>
                        value !== undefined && value !== null && value.trim() !== ""
                    )
                    .map((value) => parseFloat(value) || 0);

                  // Mark all matching inputs as source fields
                  matchingInputs.forEach((input) => {
                    this.markSourceField(input.id);
                  });

                  if (values.length > 0) {
                    const total = this.roundToTwo(
                      values.reduce((sum, value) => sum + value, 0)
                    );

                    if (parseFloat(destinationElement.value) !== total) {
                      this.highlightField(destinationElement);

                      // Usar updateFieldValue para actualizar correctamente el campo y notificar a Vue
                      this.updateFieldValue(rule.destination, total, destinationElement);
                    }
                  }
                }
              }
          }
        });
      } catch (error) {
        console.error("Error processing rules:", error);
      } finally {
        this.isProcessing = false;
        this.isUpdatingCalculatedField = false; // Desmarcar el flag
        this.processingFields.clear();
      }
    },

    /**
     * Clear all rules and destination fields
     */
    clearRules() {
      this.destinationFields.forEach((fieldId) => {
        const field = document.getElementById(fieldId);
        if (field) {
          field.classList.remove("calculated-field");
          //field.readOnly = false;
          field.title = "";

          // Remove calculation display
          const display = field.parentNode.querySelector(
            ".calculation-display"
          );
          if (display) {
            display.remove();
          }
        }
      });

      // Clear source field indicators
      this.clearSourceFieldIndicators();

      this.rulesengines = [];
      this.destinationFields.clear();
      this.executedRules.clear();
      this.processingFields.clear();

      // Limpiar control de ejecución
      this.ruleExecutionCount.clear();
      this.currentExecutionCycle = 0;
    },

    /**
     * Clear execution control to allow rule re-execution
     */
    clearExecutionControl() {
      this.executedRules.clear();
      this.ruleExecutionCount.clear();
      this.currentExecutionCycle = 0;
    },

    /**
     * Reset execution control for a new processing cycle
     */
    resetExecutionControl() {
      this.ruleExecutionCount.clear();
      this.currentExecutionCycle = 0;
      this.executedRules.clear();
    },

    /**
     * Get execution statistics for debugging
     */
    getExecutionStats() {
      return {
        currentCycle: this.currentExecutionCycle,
        totalRules: this.rulesengines.length,
        executedRules: this.executedRules.size,
        executionCounts: Object.fromEntries(this.ruleExecutionCount),
      };
    },

    /**
     * Allow rules to be executed again (useful when input data changes)
     */
    allowRuleReExecution() {
      this.resetExecutionControl();
    },

    /**
     * Check if a rule can be executed
     */
    canExecuteRule(ruleId) {
      const executionCount = this.ruleExecutionCount.get(ruleId) || 0;
      return executionCount < this.maxRuleExecutions;
    },

    /**
     * Safely dispatch events to avoid recursion
     */
    safeDispatchEvent(element, eventType) {
      if (!this.isUpdatingCalculatedField) {
        const event = new Event(eventType, { bubbles: true });
        element.dispatchEvent(event);
      }
    },

    /**
     * Detect infinite loop and prevent it
     */
    detectInfiniteLoop(ruleId) {
      const executionCount = this.ruleExecutionCount.get(ruleId) || 0;
      if (executionCount > 10) {
        // Si una regla se ejecuta más de 10 veces, hay un bucle
        return true;
      }
      return false;
    },

    /**
     * Extract source fields from a rule pattern
     */
    extractSourceFields(pattern) {
      const fields = new Set();

      // Dividir por operadores matemáticos
      const parts = pattern.split(/[\+\-\*\/\(\)]/);

      parts.forEach((part) => {
        const trimmed = part.trim();
        if (trimmed && !["", "X"].includes(trimmed)) {
          fields.add(trimmed);
        }
      });

      return Array.from(fields);
    },

    /**
     * Check if a field change should trigger rule re-execution
     */
    shouldReExecuteRules(fieldId) {
      return this.rulesengines.some((rule) => {
        const sourceFields = this.extractSourceFields(rule.pattern);
        return sourceFields.includes(fieldId);
      });
    },

    /**
     * Mark a field as changed to allow rule re-execution
     */
    markFieldChanged(fieldId) {
      // Cuando un campo cambia, verificar si afecta a alguna regla
      if (this.shouldReExecuteRules(fieldId)) {
        // Limpiar el control de ejecución para permitir re-ejecución
        this.rulesengines.forEach((rule) => {
          const sourceFields = this.extractSourceFields(rule.pattern);
          if (sourceFields.includes(fieldId)) {
            const ruleId = `${rule.pattern}-${rule.destination}-${rule.condition}`;
            // Limpiar el conteo de ejecución para permitir re-ejecución
            this.ruleExecutionCount.delete(ruleId);
            this.executedRules.delete(ruleId);
          }
        });

        // Procesar las reglas nuevamente después de un breve delay
        this.$nextTick(() => {
          if (!this.isProcessing) {
            this.processRules();
          }
        });
      }
    },

    /**
     * Force re-execution of all rules (use with caution)
     */
    forceReExecution() {
      this.clearExecutionControl();
      this.processRules();
    },

    /**
     * Actualizar un campo y notificar a Vue correctamente
     * @param {string} fieldId - El ID del campo
     * @param {any} value - El valor a establecer
     * @param {HTMLElement} element - El elemento del input (opcional)
     */
    updateFieldValue(fieldId, value, element = null) {
      try {
        const fieldElement = element || document.getElementById(fieldId);
        if (!fieldElement) {
          if (fieldId.includes('total_invoicesupplier')) {
            console.warn("🔍 [DEBUG total_invoicesupplier] Campo no encontrado:", fieldId);
          }
          return;
        }

        // Debug específico solo para total_invoicesupplier
        const isTotalInvoiceSupplier = fieldId.includes('total_invoicesupplier');
        if (isTotalInvoiceSupplier) {
          console.log("🔄 [DEBUG total_invoicesupplier] Actualizando campo calculado:", {
            fieldId,
            valorAnterior: fieldElement.value,
            valorNuevo: value,
            tipo: typeof value
          });
        }

        // Actualizar el valor en el DOM
        fieldElement.value = value;

        // Buscar el componente FormV2 o Form que contiene este campo
        // IMPORTANTE: Buscar el componente FormV2/Form principal, no FormInput
        let formComponent = null;
        let currentElement = fieldElement;
        let searchDepth = 0;
        const maxDepth = 50; // Aumentar profundidad para encontrar FormV2

        while (currentElement && !formComponent && searchDepth < maxDepth) {
          if (currentElement.__vue__) {
            const vueInstance = currentElement.__vue__;
            // Buscar por nombre del componente primero (más confiable)
            const componentName = vueInstance.$options.name;
            if (componentName === 'FormV2' || componentName === 'Form') {
              // Verificar que tenga formData (es el componente principal, no un subcomponente)
              if (vueInstance.formData !== undefined) {
                formComponent = vueInstance;
                break;
              }
            }
            // También buscar por método onFieldChange, pero solo si NO es FormInput
            if (vueInstance.onFieldChange && 
                typeof vueInstance.onFieldChange === 'function' &&
                componentName !== 'FormInput') {
              // Verificar que tenga formData para asegurarnos de que es el componente principal
              if (vueInstance.formData !== undefined) {
                formComponent = vueInstance;
                break;
              }
            }
          }
          currentElement = currentElement.parentElement;
          searchDepth++;
        }

        // Si encontramos el componente Form, extraer el fieldKey del fieldId y llamar a onFieldChange
        if (formComponent && formComponent.onFieldChange) {
          // Extraer el fieldKey del fieldId
          // El formato puede ser: "fieldKey", "groupKey-fieldKey", "groupKey-1-fieldKey", etc.
          const idParts = fieldId.split('-');
          let fieldKey = idParts[idParts.length - 1]; // Por defecto, el último segmento
          
          // Si hay un idPrefix, intentar extraer el fieldKey correctamente
          if (formComponent.idPrefix) {
            const prefixParts = formComponent.idPrefix.split('-');
            // Verificar si el fieldId comienza con el prefix
            let matches = true;
            for (let i = 0; i < prefixParts.length && i < idParts.length; i++) {
              if (idParts[i] !== prefixParts[i]) {
                matches = false;
                break;
              }
            }
            if (matches && idParts.length > prefixParts.length) {
              // El fieldKey es el último segmento después del prefix
              fieldKey = idParts[idParts.length - 1];
            }
          }

          // Debug específico solo para total_invoicesupplier
          const isTotalInvoiceSupplier = fieldId.includes('total_invoicesupplier');
          if (isTotalInvoiceSupplier) {
            console.log("✅ [DEBUG total_invoicesupplier] Llamando a onFieldChange:", {
              fieldId,
              fieldKey,
              value,
              componentName: formComponent.$options.name,
              tieneFormData: !!formComponent.formData,
              valorEnFormData: formComponent.formData ? formComponent.formData[fieldKey] : undefined,
              tieneGroupInvoicesupplier: formComponent.formData ? !!formComponent.formData.group_invoicesupplier : false,
              valorEnGroup: formComponent.formData && formComponent.formData.group_invoicesupplier ? formComponent.formData.group_invoicesupplier[fieldKey] : undefined
            });
          }

          // Llamar a onFieldChange para que actualice el formData correctamente
          formComponent.onFieldChange(fieldKey, value);
        } else {
          const isTotalInvoiceSupplier = fieldId.includes('total_invoicesupplier');
          if (isTotalInvoiceSupplier) {
            console.warn("⚠️ [DEBUG total_invoicesupplier] No se encontró componente Form, usando fallback:", {
              fieldId,
              value
            });
          }
          // Si no encontramos el componente Form, usar updateFormDataForNestedField como fallback
          this.updateFormDataForNestedField(fieldId, value);
        }

        // Disparar eventos nativos para compatibilidad
        if (!this.isUpdatingCalculatedField) {
          const changeEvent = new Event("change", { bubbles: true });
          fieldElement.dispatchEvent(changeEvent);
          const inputEvent = new Event("input", { bubbles: true });
          fieldElement.dispatchEvent(inputEvent);
        }
      } catch (error) {
        console.error("Error updating field value:", error);
        // Fallback: usar updateFormDataForNestedField
        this.updateFormDataForNestedField(fieldId, value);
      }
    },

    /**
     * Actualizar formData cuando un campo calculado está dentro de un grupo anidado
     * @param {string} fieldId - El ID del campo (ej: "products-1-total" o "groupKey-1-subFieldKey")
     * @param {any} value - El valor calculado
     */
    updateFormDataForNestedField(fieldId, value) {
      try {
      

        // Buscar el elemento del campo en el DOM
        const fieldElement = document.getElementById(fieldId);
        if (!fieldElement) {
         
          return;
        }

       

        // Buscar el componente Vue padre que contiene el grupo
        // IMPORTANTE: Necesitamos encontrar el Form PADRE (no el anidado) que tiene el array completo
        let currentElement = fieldElement;
        let formComponent = null;
        let nestedFormComponent = null; // El Form anidado (solo tiene un producto)
        let searchDepth = 0;
        const maxDepth = 30;

        // Primero encontrar el Form anidado (el que tiene el idPrefix)
        while (currentElement && !nestedFormComponent && searchDepth < maxDepth) {
          if (currentElement.__vue__) {
            const vueInstance = currentElement.__vue__;
            if ((vueInstance.$options.name === 'Form' || vueInstance.formData !== undefined) && 
                vueInstance.idPrefix && vueInstance.idPrefix.includes('-')) {
              nestedFormComponent = vueInstance;
            
              break;
            }
          }
          currentElement = currentElement.parentElement;
          searchDepth++;
        }

        // Ahora buscar el Form PADRE que tiene el array completo
        // Si tenemos el groupKey del Form anidado, buscar el Form que tiene ese grupo
        currentElement = fieldElement;
        searchDepth = 0;
        while (currentElement && !formComponent && searchDepth < maxDepth) {
          if (currentElement.__vue__) {
            const vueInstance = currentElement.__vue__;
            // Buscar el Form que tiene el array (puede ser products, productandprice, etc.)
            if ((vueInstance.$options.name === 'Form' || vueInstance.formData !== undefined) && 
                vueInstance.formData) {
              // Si ya tenemos el groupKey, verificar si este Form lo tiene
              if (groupKey) {
                const groupParts = groupKey.split('-');
                const lastSegment = groupParts[groupParts.length - 1]; // "productandprice"
                
                // Intentar con el último segmento primero
                if (vueInstance.formData[lastSegment] && Array.isArray(vueInstance.formData[lastSegment])) {
                  formComponent = vueInstance;
                  break;
                }
                
                // También verificar si está dentro de un grupo padre (ej: group_proform.productandprice)
                if (groupParts.length > 1) {
                  const parentGroup = groupParts[0]; // "group_proform"
                  const childGroup = groupParts[groupParts.length - 1]; // "productandprice"
                  if (vueInstance.formData[parentGroup] && 
                      vueInstance.formData[parentGroup][childGroup] && 
                      Array.isArray(vueInstance.formData[parentGroup][childGroup])) {
                    formComponent = vueInstance;
                    break;
                  }
                }
              }
              
              // Fallback: buscar cualquier Form que tenga grupos de tipo array según modelFields
              if (!formComponent) {
                // Buscar grupos que son arrays según modelFields (this.fields)
                if (this.fields && Array.isArray(this.fields)) {
                  // Buscar el primer grupo que es array y existe en formData
                  for (const field of this.fields) {
                    if (field.type === 'group' && field.isArray === true) {
                      const groupKey = field.key;
                      if (vueInstance.formData[groupKey] && Array.isArray(vueInstance.formData[groupKey])) {
                        formComponent = vueInstance;
                        break;
                      }
                    }
                  }
                }
                
                // Si aún no encontramos, buscar cualquier array en formData (último recurso)
                if (!formComponent) {
                  const arrayKeys = Object.keys(vueInstance.formData).filter(key => 
                    Array.isArray(vueInstance.formData[key])
                  );
                  if (arrayKeys.length > 0) {
                    formComponent = vueInstance;
                    break;
                  }
                }
              }
            }
          }
          currentElement = currentElement.parentElement;
          searchDepth++;
        }

        if (!formComponent || !formComponent.formData) {
          return;
        }

        // Extraer el fieldKey del ID del campo
        // El formato puede ser: "fieldKey", "groupKey-fieldKey", "groupKey-1-fieldKey", etc.
        const idParts = fieldId.split('-');
        
        // Si hay un Form anidado con idPrefix, usarlo para extraer el índice
        let relevantParts = idParts;
        let groupKey = null;
        let groupIndex = null;

        if (nestedFormComponent && nestedFormComponent.idPrefix) {
          // El idPrefix del Form anidado puede ser:
          // - "products-1" (simple)
          // - "group_proform-productandprice-1" (anidado)
          const prefixParts = nestedFormComponent.idPrefix.split('-');
          
          // Buscar el último número que sea el índice
          let foundIndex = false;
          for (let i = prefixParts.length - 1; i >= 0; i--) {
            const num = parseInt(prefixParts[i]);
            if (!isNaN(num) && num > 0) {
              groupIndex = num;
              // Todo lo anterior es el groupKey (puede incluir múltiples niveles)
              groupKey = prefixParts.slice(0, i).join('-');
              foundIndex = true;
              break;
            }
          }
          
          // Si no encontramos un índice numérico, intentar con el último segmento
          if (!foundIndex && prefixParts.length >= 2) {
            const lastPart = prefixParts[prefixParts.length - 1];
            const num = parseInt(lastPart);
            if (!isNaN(num)) {
              groupIndex = num;
              groupKey = prefixParts.slice(0, -1).join('-');
            }
          }
        } else if (formComponent && formComponent.idPrefix) {
          // Si no hay Form anidado, intentar con el idPrefix del Form padre
          const prefixParts = formComponent.idPrefix.split('-');
          if (idParts.length >= prefixParts.length) {
            let matches = true;
            for (let i = 0; i < prefixParts.length; i++) {
              if (idParts[i] !== prefixParts[i]) {
                matches = false;
                break;
              }
            }
            if (matches) {
              relevantParts = idParts.slice(prefixParts.length);
            }
          }
        }

        const fieldKey = relevantParts[relevantParts.length - 1] || idParts[idParts.length - 1]; // El último segmento es el fieldKey

        // DETECTAR SI EL CAMPO PERTENECE AL GRUPO EXTERNO O AL ELEMENTO DEL ARRAY
        // IMPORTANTE: Si tenemos groupIndex, el campo pertenece a un elemento específico del array
        // NO al nivel raíz, incluso si existe en el nivel raíz
        // Solo actualizar en nivel raíz si NO hay groupIndex (campo del grupo completo, no de un elemento)
        
        // Si NO hay groupIndex, el campo pertenece al nivel raíz del grupo (no a un elemento específico)
        // Solo en este caso actualizar en el nivel raíz
        if (groupIndex === null || groupIndex === undefined) {
          const isFieldAtRootLevel = formComponent.formData.hasOwnProperty(fieldKey);
          
          if (isFieldAtRootLevel) {
            const oldValue = formComponent.formData[fieldKey];
            
            // Marcar que estamos actualizando un campo calculado para evitar que el watcher lo sobrescriba
            if (!formComponent._updatingCalculatedFields) {
              formComponent._updatingCalculatedFields = new Set();
            }
            formComponent._updatingCalculatedFields.add(fieldKey);
            
            formComponent.$set(formComponent.formData, fieldKey, value);
            formComponent.$emit("input", formComponent.formData);
            
            // Remover la marca después de un breve delay para permitir que el watcher procese
            setTimeout(() => {
              if (formComponent._updatingCalculatedFields) {
                formComponent._updatingCalculatedFields.delete(fieldKey);
              }
            }, 100);
            return;
          }
        }
        
        // Si hay groupIndex, el campo pertenece a un elemento específico del array
        // Actualizar dentro del elemento del array correspondiente
        if (groupKey && groupIndex !== null && groupIndex !== undefined && !isNaN(groupIndex)) {
          // El groupKey puede ser anidado (ej: "group_proform-productandprice")
          // El último segmento es el nombre del array que necesitamos actualizar
          const groupParts = groupKey.split('-');
          const arrayKey = groupParts[groupParts.length - 1]; // "productandprice"
          
          let targetArray = null;
          let finalGroupKey = null;
          
          // Buscar el array usando modelFields (this.fields) para encontrar grupos que son arrays
          // Primero intentar con el último segmento del groupKey (nombre del grupo)
          if (formComponent.formData[arrayKey] && Array.isArray(formComponent.formData[arrayKey])) {
            targetArray = formComponent.formData[arrayKey];
            finalGroupKey = arrayKey;
          }
          // Si no está directamente, buscar en modelFields todos los grupos que son arrays
          else if (this.fields && Array.isArray(this.fields)) {
            for (const field of this.fields) {
              if (field.type === 'group' && field.isArray === true) {
                const groupKey = field.key;
                if (formComponent.formData[groupKey] && Array.isArray(formComponent.formData[groupKey])) {
                  targetArray = formComponent.formData[groupKey];
                  finalGroupKey = groupKey;
                  break;
                }
              }
            }
          } 
          // Si no está directamente, puede estar anidado dentro de otro grupo
          else if (groupParts.length > 1) {
            // Por ejemplo: formData.group_proform.productandprice
            let current = formComponent.formData;
            let path = [];
            for (let i = 0; i < groupParts.length; i++) {
              if (current && current[groupParts[i]]) {
                path.push(groupParts[i]);
                current = current[groupParts[i]];
                if (Array.isArray(current) && i === groupParts.length - 1) {
                  targetArray = current;
                  finalGroupKey = arrayKey; // Usar solo el último segmento para $set
                  break;
                }
              } else {
                break;
              }
            }
          }
          
          if (targetArray && finalGroupKey && targetArray[groupIndex - 1]) {
            const groupItem = targetArray[groupIndex - 1];
            if (groupItem && typeof groupItem === 'object') {
              const oldValue = groupItem[fieldKey];
              
              // Actualizar el campo en el item
              formComponent.$set(groupItem, fieldKey, value);
              
              // Forzar actualización del array completo para que Vue detecte el cambio
              formComponent.$set(formComponent.formData, finalGroupKey, [...targetArray]);
              
              // Emitir el cambio
              formComponent.$emit("input", formComponent.formData);
              return;
            }
          }
        }

        // Si hay más de una parte, puede estar dentro de un grupo
        if (relevantParts.length > 1) {
          // Intentar encontrar el índice del grupo (si existe)
          const possibleIndex = parseInt(relevantParts[relevantParts.length - 2]);
          
          if (!isNaN(possibleIndex) && relevantParts.length >= 3) {
            // Está dentro de un grupo con índice: "groupKey-1-fieldKey"
            const groupKeyFromParts = relevantParts.slice(0, -2).join('-');
            
            // Buscar el nombre correcto del grupo según modelFields
            // groupKeyFromParts puede ser "group_proform-productandprice", necesitamos "productandprice"
            const groupKeyParts = groupKeyFromParts.split('-');
            const lastPart = groupKeyParts[groupKeyParts.length - 1]; // "productandprice"
            
            // Buscar en modelFields para encontrar el nombre correcto del grupo
            let actualGroupKey = null;
            if (this.fields && Array.isArray(this.fields)) {
              const modelField = this.fields.find(f => 
                f.type === 'group' && 
                f.isArray === true && 
                (f.key === lastPart || f.key === groupKeyFromParts)
              );
              if (modelField) {
                actualGroupKey = modelField.key;
              }
            }
            
            // Si no encontramos en modelFields, usar el último segmento
            if (!actualGroupKey) {
              actualGroupKey = lastPart;
            }
            
            // Verificar si el grupo existe en formData
            if (formComponent.formData[actualGroupKey] && 
                Array.isArray(formComponent.formData[actualGroupKey]) &&
                formComponent.formData[actualGroupKey][possibleIndex - 1]) {
              // Actualizar el campo dentro del grupo
              const groupItem = formComponent.formData[actualGroupKey][possibleIndex - 1];
              if (groupItem && typeof groupItem === 'object') {
                const oldValue = groupItem[fieldKey];
                formComponent.$set(groupItem, fieldKey, value);
                // Forzar actualización del array completo
                formComponent.$set(formComponent.formData, actualGroupKey, [...formComponent.formData[actualGroupKey]]);
                // Emitir el cambio
                formComponent.$emit("input", formComponent.formData);
                return;
              }
            }
          } else {
            // Está dentro de un grupo simple (no array): "groupKey-fieldKey"
            const groupKey = relevantParts.slice(0, -1).join('-');
            
            // Verificar si el grupo existe en formData
            if (formComponent.formData[groupKey] && 
                typeof formComponent.formData[groupKey] === 'object' &&
                !Array.isArray(formComponent.formData[groupKey])) {
              // Actualizar el campo dentro del grupo
              formComponent.$set(formComponent.formData[groupKey], fieldKey, value);
              // Emitir el cambio
              formComponent.$emit("input", formComponent.formData);
              return;
            }
          }
        }

        // Si no está dentro de un grupo, actualizar directamente en formData
        if (formComponent.formData.hasOwnProperty(fieldKey)) {
          formComponent.$set(formComponent.formData, fieldKey, value);
          formComponent.$emit("input", formComponent.formData);
        }
      } catch (error) {
        console.error("Error updating formData for nested field:", error);
      }
    },
  },

  mounted() {
    // Add global styles
    const style = document.createElement("style");
    style.textContent = `
      .calculated-field,
      .readonly-field {
        background-color: #f8f9fa !important;
        cursor: not-allowed !important;
        pointer-events: none !important;
        -webkit-user-select: none;
        user-select: none;
      }

      .calculated-field:hover::before {
        content: "Campo calculado automáticamente";
        position: absolute;
        background: #333;
        color: white;
        padding: 5px;
        border-radius: 3px;
        font-size: 0.8em;
        z-index: 1000;
      }

    


      .field-highlight {
        animation: highlight-field 0.5s ease-out;
        border: 2px solid;
        border-color: #002cff !important;
      }

      @keyframes highlight-field {
        0% {
          border-color: #002cff;
          color: white;
        }
        100% {
          border-color: #002cff;
          color: inherit;
        }
      }
    `;
    document.head.appendChild(style);
  },
};
